import { useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, ErrorState } from '@react-apps/design-system';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  STORED_FILE_STATUS_LABELS,
  type StoredFileDetail,
} from '../../domain/entities/stored-file.entity';
import { FilePreviewCard } from '../components/FilePreviewCard';
import { ForceDeleteFileModal } from '../components/ForceDeleteFileModal';
import { LegalHoldModal } from '../components/LegalHoldModal';
import { describeFileError, formatBytes, formatDateTime } from '../format';
import { useStoredFile } from '../hooks/use-stored-files';
import { OwnerLink } from '../owner-link';

const LIST_PATH = '/files';

export function StoredFileDetailPage() {
  const { fileId = '' } = useParams();
  const { data, isLoading, error, refetch } = useStoredFile(fileId);
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <BackLink />
      {isLoading ? (
        <div
          aria-busy="true"
          className="border-(--color-border) bg-(--color-bg-primary) h-96 animate-pulse rounded-xl border"
        />
      ) : data ? (
        // Com dado em cache, o refetch que falhou (o 503 do force-delete, por
        // exemplo) não tira a tela nem o modal com a mensagem do erro.
        <FileDetail file={data} />
      ) : status === 404 ? (
        <ErrorState message="Este arquivo não existe ou está fora do seu escopo. Volte para a lista e escolha outro." />
      ) : (
        <ErrorState
          message={describeFileError(
            error,
            'Não foi possível carregar o arquivo.',
          )}
          onRetry={() => void refetch()}
        />
      )}
    </div>
  );
}

function BackLink() {
  const goBack = useBackNavigate(LIST_PATH);
  return (
    <button
      type="button"
      onClick={goBack}
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm transition-colors"
    >
      ← Arquivos
    </button>
  );
}

function FileDetail({ file }: { file: StoredFileDetail }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [deleting, setDeleting] = useState(false);
  const [holding, setHolding] = useState(false);
  const deleted = file.status === 'deleted';

  const onDeleted = () => {
    setDeleting(false);
    // O modal devolve o foco ao botão que o abriu, e o botão some com o
    // arquivo apagado: o foco vai para o título, depois da devolução do modal.
    setTimeout(() => headingRef.current?.focus());
  };

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-(--color-text-primary) text-2xl font-semibold outline-none"
          >
            Arquivo
          </h1>
          <p className="text-(--color-text-secondary) mt-1 font-mono text-xs">
            {file.fileId}
          </p>
        </div>
        {/* Arquivo apagado não tem mais ação: nem apagar, nem legal hold. */}
        {!deleted && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => setHolding(true)}>
              {file.legalHold ? 'Desligar legal hold' : 'Ligar legal hold'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setDeleting(true)}
              disabled={file.legalHold}
              aria-describedby={
                file.legalHold ? 'file-delete-blocked' : undefined
              }
            >
              Apagar arquivo
            </Button>
          </div>
        )}
      </div>
      {file.legalHold && !deleted && (
        <p
          id="file-delete-blocked"
          className="text-(--color-text-secondary) text-sm"
        >
          Para apagar, desligue o legal hold antes.
        </p>
      )}

      {/* Apagado e sumido não têm objeto no bucket para abrir. */}
      {(file.status === 'active' || file.status === 'superseded') && (
        // A rota reaproveita o componente quando só o `:fileId` muda; sem a
        // `key`, a imagem aberta de um arquivo ficaria na tela do outro.
        <FilePreviewCard key={file.fileId} file={file} />
      )}

      <dl className="border-(--color-border) bg-(--color-bg-primary) grid gap-x-6 gap-y-4 rounded-xl border p-6 sm:grid-cols-2">
        <Field label="Nome original">{file.name}</Field>
        <Field label="Path">
          <span className="break-all font-mono text-xs">{file.path}</span>
        </Field>
        <Field label="Status">
          <Badge
            variant={
              deleted
                ? 'error'
                : file.status === 'active'
                  ? 'success'
                  : 'warning'
            }
          >
            {STORED_FILE_STATUS_LABELS[file.status] ?? file.status}
          </Badge>{' '}
          desde {formatDateTime(file.inThisStatusSince)}
        </Field>
        <Field label="Legal hold">
          {file.legalHold ? 'Ligado' : 'Desligado'}
        </Field>
        <Field label="Dono">
          <OwnerLink
            module={file.module}
            ownerType={file.ownerType}
            ownerId={file.ownerId}
          />
        </Field>
        <Field label="BU">{file.tenant ?? 'Sem BU'}</Field>
        <Field label="Bucket">{file.bucket}</Field>
        <Field label="Tipo">{file.mime}</Field>
        <Field label="Tamanho">{formatBytes(file.sizeBytes)}</Field>
        <Field label="Enviado por">
          <span className="font-mono text-xs">{file.uploadedBy ?? '—'}</span>
        </Field>
        <Field label="Último acesso">
          {formatDateTime(file.lastResolvedAt)} ({file.resolveCount}{' '}
          {file.resolveCount === 1 ? 'acesso' : 'acessos'})
        </Field>
        <Field label="Expira em">{formatDateTime(file.expiresAt)}</Field>
        <Field label="Apagado em">{formatDateTime(file.deletedAt)}</Field>
        <Field label="Criado em">{formatDateTime(file.createdAt)}</Field>
        <Field label="Atualizado em">{formatDateTime(file.updatedAt)}</Field>
      </dl>

      <ForceDeleteFileModal
        fileId={file.fileId}
        legalHold={file.legalHold}
        open={deleting}
        onClose={() => setDeleting(false)}
        onDeleted={onDeleted}
      />
      <LegalHoldModal
        fileId={file.fileId}
        legalHold={file.legalHold}
        open={holding}
        onClose={() => setHolding(false)}
      />
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-(--color-text-secondary) text-xs">{label}</dt>
      <dd className="text-(--color-text-primary) mt-1 text-sm">{children}</dd>
    </div>
  );
}
